import useProductsV2 from '@/api/inventory-management/products/useProductsV2';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Product } from '@/components/common/semantic-icons';
import { cn } from '@/lib/utils';
import { ProductV2DTO } from '@/types/application/inventory-management/ProductV2DTO';
import { useGT } from 'gt-react';
import { useState } from 'react';

function ProductV2Item({
  product,
  isTrigger = false,
}: {
  product: ProductV2DTO;
  isTrigger?: boolean;
}) {
  const gt = useGT();

  return (
    <div className="flex items-center gap-1">
      <Avatar className="size-6">
        <AvatarImage src={product.imageUrl ?? undefined} />
        <AvatarFallback>
          <Product />
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{product.name}</span>
        {!isTrigger && (
          <span className="text-xs text-muted-foreground">
            {product.baseSku ||
              gt('No SKU', {
                context:
                  'product autocomplete fallback text when a product has no SKU assigned',
              })}
          </span>
        )}
      </div>
    </div>
  );
}

export function ProductsV2Autocomplete({
  onProductSelect,
  selectedProductId,
  allowClear = false,
  className,
}: {
  onProductSelect: (productId?: string | undefined) => void;
  selectedProductId?: string;
  allowClear?: boolean;
  className?: string;
}) {
  const gt = useGT();
  // The typed query renders at once; the catalog is queried 150ms after it
  // settles (`onSearch`).
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [apiSearchQuery, setApiSearchQuery] = useState('');

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsLoadError,
  } = useProductsV2(
    {
      isSellable: true,
      page: 1,
      pageSize: 50,
      // `search` matches name, SKU, description, and origin id — so typing an
      // item id/SKU fragment finds the product (not just its name).
      search: apiSearchQuery.length > 0 ? apiSearchQuery : undefined,
    },
    true,
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Combobox
        items={productsData?.edges ?? []}
        value={selectedProductId || null}
        onValueChange={(next) => onProductSelect(next ?? '')}
        deselectable
        disabled={isLoadingProducts}
        getValue={(product) => product.id}
        getLabel={(product) => product.name}
        // The server already filtered by name/SKU/description/origin id, so
        // `onSearch` turns the client re-filter (name only) off.
        search={productSearchQuery}
        onSearchChange={setProductSearchQuery}
        onSearch={setApiSearchQuery}
        debounceMs={150}
        pinSelected={false}
        resetSearchOnClose={false}
        indicator="none"
        renderItem={(product) => <ProductV2Item product={product} />}
        renderValue={({ selected }) => (
          <ProductV2Item product={selected[0]} isTrigger />
        )}
        allowClear={allowClear}
        className={cn(productsLoadError && 'border-destructive')}
        contentClassName="w-full"
        labels={pickerLabels({
          placeholder: gt('Search for a product', {
            context: 'product autocomplete search input placeholder',
          }),
          search: gt('Search options...', {
            context: 'search box placeholder inside a picker dropdown',
          }),
        })}
      />
    </div>
  );
}
